import { FC, useEffect } from 'react';
import {
  Avatar,
  Button,
  Divider,
  Field,
  Icon,
  Select,
  SocialMediaIcon,
  SocialMediaIconProps,
  TextArea,
  TextInput,
  Tooltip,
  Alert,
} from '@kontentino/ui';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import config from 'config';
import useBoolean from 'utils/hooks/useBoolean';
import BrandHubSuggestProfileModal from '../BrandHubSuggestProfileModal';
import { useTranslation } from 'react-i18next';
import { GenerationsApi } from 'app/modules/posts/api/GenerationsApi';
import { AiModelInfo, BrandProfile } from 'app/modules/brandHub/types';
import ValidationScheme from 'app/constants/validationSchemes';
import { formatDateTime } from 'app/utils/date';
import Popup from 'utils/popup';
import FeatureBadge from 'app/components/FeatureBadge';
import { useToast } from 'app/hooks/useToast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import Modal from 'components/shared/Modal';
import SingleImageFilePicker from 'app/components/SingleImageFilePicker';
import { faChartLine } from '@fortawesome/pro-regular-svg-icons/faChartLine';
import PageTypeUtils from 'app/utils/pageType';
import TimeUtils from 'app/utils/time';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';

const analyticsProfileSchema = z.object({
  profileId: z.string(),
  pageType: z.number(),
});

const formSchema = z.object({
  id: z.string().optional(),
  name: ValidationScheme.RequiredString(),
  usp: ValidationScheme.RequiredString(),
  audience: ValidationScheme.RequiredString(),
  language: ValidationScheme.RequiredString(),
  story: ValidationScheme.RequiredString(),
  // Optional string fields — backend serves null when the field hasn't
  // been populated (either the agent didn't generate it, or the legacy
  // record predates the field). Accept null/undefined and coerce to ''
  // so the form input handles them as empty strings. Was rejecting
  // null → form validation failed → Save button permanently disabled
  // (tester report #10 / #16).
  otherInfo: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  textSample: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  logoSrc: z.instanceof(File).or(z.string()).nullable(),
  analyticsProfiles: z.array(analyticsProfileSchema).optional().default([]),
  uspKeywords: z.array(z.string()).optional().default([]),
  audienceKeywords: z.array(z.string()).optional().default([]),
  languageKeywords: z.array(z.string()).optional().default([]),
  storyKeywords: z.array(z.string()).optional().default([]),
  otherInfoKeywords: z.array(z.string()).optional().default([]),
  brandValues: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  brandValuesKeywords: z.array(z.string()).optional().default([]),
  coreValues: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  coreValuesKeywords: z.array(z.string()).optional().default([]),
  toneOfVoice: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  toneOfVoiceKeywords: z.array(z.string()).optional().default([]),
  wordsToEmbrace: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  wordsToEmbraceKeywords: z.array(z.string()).optional().default([]),
  // Optional — agent may return null. Coerce to '' so the inferred
  // FormValues type stays compatible with BrandProfile (string, not
  // string|null) and the form input handles it as an empty string.
  visualIdentity: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  visualIdentityKeywords: z.array(z.string()).optional().default([]),
  mainCompetitors: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  mainCompetitorsKeywords: z.array(z.string()).optional().default([]),
  website: z
    .string()
    .optional()
    .nullable()
    .transform((v) => v ?? undefined),
  contentFormat: z.array(z.string()).optional().default([]),
  topics: z.array(z.string()).optional().default([]),
  profileGroupId: z.number().nullable().optional(),
  aiChatModel: z.string().nullable().optional(),
  aiImageModel: z.string().nullable().optional(),
});

const defaultValues = {
  id: '',
  name: '',
  usp: '',
  audience: '',
  language: '',
  story: '',
  otherInfo: '',
  textSample: '',
  logoSrc: null,
  analyticsProfiles: [],
  uspKeywords: [],
  audienceKeywords: [],
  languageKeywords: [],
  storyKeywords: [],
  otherInfoKeywords: [],
  brandValues: '',
  brandValuesKeywords: [],
  coreValues: '',
  coreValuesKeywords: [],
  toneOfVoice: '',
  toneOfVoiceKeywords: [],
  wordsToEmbrace: '',
  wordsToEmbraceKeywords: [],
  visualIdentity: '',
  visualIdentityKeywords: [],
  mainCompetitors: '',
  mainCompetitorsKeywords: [],
  website: '',
  contentFormat: [],
  topics: [],
  profileGroupId: null,
  aiChatModel: null,
  aiImageModel: null,
};

type FormValues = z.infer<typeof formSchema>;

interface Props {
  profileToEdit?: BrandProfile;
  isProfileEdit: boolean;
  onClose: () => void;
  initialValues?: Partial<FormValues>;
  skipSuggestModal?: boolean;
  focusSelect?: boolean;
  eventHandlers: {
    onProfileSave: (profile: BrandProfile) => void;
    onRemainingProfilesChange: (profiles: BrandProfile[]) => void;
  };
}

const MAX_ANALYTICS_PROFILES = 5;

const BrandHubManageProfileModalForm: FC<Props> = ({
  profileToEdit,
  isProfileEdit,
  onClose,
  eventHandlers,
  initialValues,
  skipSuggestModal = false,
  focusSelect = false,
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultValues,
      ...initialValues,
    },
  });
  const profileId = profileToEdit?.id;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [suggestProfileModalShown, setSuggestProfileModalShown] =
    useBoolean(false);
  const translateTextOptions = useQuery(
    queryKey.transformTextOptions(),
    GenerationsApi.getTransformTextOptions,
  );
  const toast = useToast();
  // const { filterActions } = useBrandHubContext();

  const isNewProfileCreation = !profileId && !isProfileEdit;

  const isFromEmptyState =
    !!initialValues && Object.keys(initialValues).length > 1;

  const shouldOpenSuggestProfileModal =
    isNewProfileCreation && !isFromEmptyState && !skipSuggestModal;

  useEffect(() => {
    if (shouldOpenSuggestProfileModal) {
      setSuggestProfileModalShown.on();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyticsProfiles = useQuery(queryKey.brandHubAnalyticsProfiles(), () =>
    BrandHubApi.getAnalyticsProfiles(),
  );

  const aiChatModelsQuery = useQuery(
    queryKey.aiModels('chat'),
    () => BrandHubApi.getAiModels('chat'),
    {
      staleTime: TimeUtils.calcMilliseconds.minutes(5),
    },
  );

  const aiImageModelsQuery = useQuery(
    queryKey.aiModels('image'),
    () => BrandHubApi.getAiModels('image'),
    {
      staleTime: TimeUtils.calcMilliseconds.minutes(5),
    },
  );

  // Helper to extract provider name from model ID (e.g., "router/openai/gpt-5" -> "OpenAI")
  const getProviderFromModelId = (modelId: string): string => {
    const parts = modelId.split('/');
    if (parts.length >= 2) {
      const provider = parts[1];
      const providerNames: Record<string, string> = {
        openai: 'OpenAI',
        google: 'Google',
        anthropic: 'Anthropic',
        'x-ai': 'xAI',
        deepseek: 'DeepSeek',
        'black-forest-labs': 'BFL',
      };
      return providerNames[provider] || provider;
    }
    return '';
  };

  // Helper to create grouped options for react-select
  const createGroupedModelOptions = (
    data: { featured?: AiModelInfo[]; all?: AiModelInfo[] } | undefined,
  ) => {
    if (!data) return [];
    return [
      {
        label: t('aiModels.top', 'Featured'),
        options: (data.featured || []).map((model) => ({
          value: model.id,
          label: `${getProviderFromModelId(model.id)}: ${model.name}`,
        })),
      },
      {
        label: t('aiModels.newest', 'All Models'),
        options: (data.all || []).map((model) => ({
          value: model.id,
          label: `${getProviderFromModelId(model.id)}: ${model.name}`,
        })),
      },
    ].filter((group) => group.options.length > 0);
  };

  const saveProfile = useMutation(BrandHubApi.saveBrandProfile, {
    onSuccess(data) {
      eventHandlers.onProfileSave(data);
      queryClient.invalidateQueries({ queryKey: ['brandHub'] });
      queryClient.invalidateQueries(queryKey.brandHubProfile(data.id));
      queryClient.invalidateQueries(queryKey.brandHubProfiles());

      onClose();
    },
    onError(e: ApiClientError) {
      toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
    },
  });

  const deleteProfile = useMutation(BrandHubApi.deleteBrandProfile, {
    onSuccess() {
      queryClient.invalidateQueries(queryKey.brandHubProfiles());
      toast('Brand profile deleted successfully', 'success');
      // const remainingProfiles =
      //   queryClient.getQueryData<BrandProfile[]>(queryKey.brandHubProfiles()) ||
      //   [];
      // filterActions.preselectProfile(remainingProfiles);
      onClose();
    },
    onError(e: ApiClientError) {
      toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
    },
  });

  useEffect(() => {
    if (profileToEdit && !form.getValues().id) {
      form.reset({
        ...profileToEdit,
        id: profileToEdit.id.toString(),
      });
    }
  }, [profileToEdit, form]);

  function onSubmit(data: FormValues) {
    saveProfile.mutate(data);
  }

  async function onDeleteProfile(id: string) {
    const { isConfirmed } = await Popup.confirm();

    if (!isConfirmed) return;

    deleteProfile.mutate(id);

    const remainingProfiles = queryClient
      .getQueryData<BrandProfile[]>(queryKey.brandHubProfiles())
      ?.filter((profile) => profile.id !== id);

    if (remainingProfiles) {
      eventHandlers.onRemainingProfilesChange(remainingProfiles);
    }
  }

  function onFormValuesSuggested({
    id,
    name,
    language,
    languageKeywords,
    ...rest
  }: BrandProfile) {
    toast(t('brandProfileSuggestedFromPosts'), 'success');
    const currentValues = form.getValues();

    form.reset({
      id: form.getValues().id,
      name: currentValues.name || name,
      language: currentValues.language || language,
      ...rest,
      // @ts-ignore
      languageKeywords: [],
    });
  }

  async function showSuggestProfileModal() {
    // const { id, name, language, logoSrc, ...overridableFields } =
    //   form.getValues();

    // const isFilled = Object.values(overridableFields).some((value) => !!value);

    // if (isFilled) {
    //   const { isConfirmed } = await Popup.confirm({
    //     title: t('generateBrandProfileTitle'),
    //     width: '420px',
    //     text: t('generateBrandProfileConfirmDescription'),
    //     confirmButtonText: t('ok'),
    //   });
    //
    //   if (!isConfirmed) return;
    // }

    setSuggestProfileModalShown.on();
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Modal.Header
        title={
          <>
            {t(profileId ? 'editBrandProfile' : 'createBrandProfile')}
            {!profileId && (
              <FeatureBadge className="tw-ml-2 tw-uppercase">
                {t('beta')}
              </FeatureBadge>
            )}
          </>
        }
        subtitle={t('fillDetailsAboutBrandProfile')}
      />
      <Modal.Content className="tw-space-y-4 tw-pb-4">
        {profileToEdit && (
          <Field.Group>
            <Field.Label required>{t('lastChange')}</Field.Label>
            <TextInput
              value={formatDateTime(profileToEdit.updatedAt)}
              disabled
            />
          </Field.Group>
        )}

        <div>
          <Button
            onClick={showSuggestProfileModal}
            size="small"
            variant="tertiary"
            data-name="brand-hub_manage-brand-profile_suggest-profile-button"
            style={{
              visibility: shouldOpenSuggestProfileModal ? 'hidden' : 'visible',
            }}
          >
            {t('generateFromMyPreviousPosts')}
          </Button>
          <BrandHubSuggestProfileModal
            isOpen={suggestProfileModalShown}
            onClose={() => {
              setSuggestProfileModalShown.off();
            }}
            onBrandProfileSuggested={onFormValuesSuggested}
          />
        </div>

        {/* Form fields */}
        <Field.Group>
          <Field.Label required>{t('brandName')}</Field.Label>
          <TextInput
            data-name="brand-hub_manage-brand-profile_name"
            placeholder={t('brandNamePlaceholder')}
            {...form.register('name')}
          />
          {form.formState.errors.name?.message && (
            <Field.Error>{t(form.formState.errors.name.message)}</Field.Error>
          )}
        </Field.Group>

        {/* Analytics Profile Selector — hidden during analytics rework */}
        {config.BRAND_HUB_ANALYTICS_ENABLED && (
          <Field.Group>
            <Field.Label>
              <Icon icon={faChartLine} className="tw-mr-1" />
              {t('analyticsProfile')}
            </Field.Label>
            <Field.Caption>
              {t('analyticsProfilePostsWillBeUsedToGenerateBrandProfile')}
            </Field.Caption>
            {analyticsProfiles.isError && (
              <Alert variant="danger" title={t('error')}>
                {t('failedToLoadAnalyticsProfiles')}
              </Alert>
            )}
            <Controller
              name="analyticsProfiles"
              control={form.control}
              render={({ field: { value, onChange } }) => {
                const profileOptions =
                  analyticsProfiles.data?.edges.map(({ node }) => {
                    const { id, name, fans, _type: type, picture } = node;
                    return {
                      value: id,
                      label: `${name}${fans ? ` (${fans} ${t('fans')})` : ''}`,
                      type,
                      picture,
                    };
                  }) || [];

                const selectedProfiles = value?.map((profile) => {
                  const matchingProfile = profileOptions.find(
                    (opt) => opt.value === profile.profileId,
                  );
                  if (!matchingProfile) {
                    return {
                      value: profile.profileId,
                      label: 'Unknown profile',
                      type: profile.pageType,
                      picture: undefined,
                    };
                  }
                  return {
                    value: profile.profileId,
                    label: matchingProfile.label,
                    type: profile.pageType,
                    picture: matchingProfile.picture,
                  };
                });

                return (
                  <div
                    data-name="brand-hub_manage-brand-profile_analytics-profile_select-wrapper"
                    data-cy="brand-hub_manage-brand-profile_analytics-profile_select-wrapper"
                  >
                    <Select
                      menuPortalTarget={document.body}
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      openMenuOnFocus={focusSelect}
                      autoFocus={focusSelect}
                      styles={{
                        menuPortal: (styles) => ({
                          ...styles,
                          zIndex: 1010,
                        }),
                      }}
                      value={selectedProfiles}
                      isClearable
                      placeholder={t('selectAnalyticsProfile')}
                      formatOptionLabel={(option) => (
                        <div className="tw-flex tw-items-center">
                          <div className="tw-relative">
                            <Avatar
                              size={26}
                              name={option.label}
                              src={option.picture ?? undefined}
                              className="tw-rounded-md"
                            />
                            <SocialMediaIcon
                              type={
                                PageTypeUtils.getName(
                                  option.type,
                                ) as SocialMediaIconProps['type']
                              }
                              size={getYtLogoGuidelineSize(12, option.type)}
                              className="tw-absolute tw-bottom-0 tw-right-0"
                            />
                          </div>
                          <span className="tw-ml-2">{option.label}</span>
                        </div>
                      )}
                      onChange={(selectedOptions) => {
                        const limitedOptions = selectedOptions.slice(
                          0,
                          MAX_ANALYTICS_PROFILES,
                        );
                        if (selectedOptions.length > MAX_ANALYTICS_PROFILES) {
                          toast(
                            t('maxAnalyticsProfilesForBrandProfileReached'),
                            'warning',
                          );
                        }
                        const analyticsProfiles = limitedOptions.map(
                          (option) => ({
                            profileId: option.value,
                            pageType: option.type,
                          }),
                        );
                        onChange(analyticsProfiles);
                      }}
                      options={profileOptions}
                      isLoading={analyticsProfiles.isLoading}
                      isOptionDisabled={(option) => {
                        const isAlreadySelected = selectedProfiles.some(
                          (profile) => profile.value === option.value,
                        );
                        return (
                          !isAlreadySelected &&
                          selectedProfiles.length >= MAX_ANALYTICS_PROFILES
                        );
                      }}
                    />
                  </div>
                );
              }}
            />
          </Field.Group>
        )}

        <Field.Group className="tw-space-y-3">
          <Field.Group>
            <Field.Label required>{t('outputLanguage')}</Field.Label>
            <Controller
              name="language"
              control={form.control}
              render={({ field: { onChange, value, ref } }) => {
                const languages =
                  translateTextOptions.data?.options.language ?? [];
                const selectedValue =
                  languages.find((language) => language.value === value) ??
                  null;

                return (
                  <div
                    ref={ref}
                    tabIndex={0}
                    data-name="brand-hub_manage-brand-profile_language_select-wrapper"
                    data-cy="brand-hub_manage-brand-profile_language_select-wrapper"
                  >
                    <Select
                      options={languages}
                      onChange={(option) => {
                        onChange(option?.value ?? null);
                      }}
                      value={selectedValue}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      styles={{
                        menuPortal: (styles) => ({
                          ...styles,
                          zIndex: 1010,
                        }),
                      }}
                      data-name="brand-hub_manage-brand-profile_language"
                      data-cy="brand-hub_manage-brand-profile_language"
                    />
                  </div>
                );
              }}
            />
            {form.formState.errors.language?.message && (
              <Field.Error>
                {t(form.formState.errors.language.message)}
              </Field.Error>
            )}
          </Field.Group>
          <Field.Group>
            <Field.Label>{t('logo')}</Field.Label>
            <Controller
              name="logoSrc"
              control={form.control}
              render={({ field: { onChange, value } }) => (
                <SingleImageFilePicker
                  imagePreview={typeof value === 'string' ? value : undefined}
                  onChange={(logo) => onChange(logo)}
                />
              )}
            />
            {form.formState.errors.name?.message && (
              <Field.Error>{t(form.formState.errors.name.message)}</Field.Error>
            )}
          </Field.Group>
          <Field.Group>
            <Field.Label required>{t('brandInfo')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_story"
              placeholder={t('storyPlaceholder')}
              {...form.register('story')}
            />
            {form.formState.errors.story?.message && (
              <Field.Error>
                {t(form.formState.errors.story.message)}
              </Field.Error>
            )}
          </Field.Group>
          <Field.Group>
            <Field.Label className="tw-capitalize">{t('website')}</Field.Label>
            <TextInput
              data-name="brand-hub_manage-brand-profile_website"
              placeholder={t('brandProfileWebsiteLinkPlaceholder')}
              {...form.register('website')}
            />
            {!!form.formState.errors.website?.message && (
              <Field.Error>
                {form.formState.errors.website?.message}
              </Field.Error>
            )}
          </Field.Group>
          <Field.Group>
            <Field.Label required>{t('usp')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_usp"
              placeholder={t('uspPlaceholder')}
              {...form.register('usp')}
            />
            {form.formState.errors.usp?.message && (
              <Field.Error>{t(form.formState.errors.usp.message)}</Field.Error>
            )}
          </Field.Group>
          <Field.Group>
            <Field.Label required>{t('targetAudience')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_audience"
              placeholder={t('targetAudiencePlaceholder')}
              {...form.register('audience')}
            />
            {form.formState.errors.audience?.message && (
              <Field.Error>
                {t(form.formState.errors.audience.message)}
              </Field.Error>
            )}
          </Field.Group>
          <Field.Group>
            <Field.Label>{t('otherInfo')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_other-info"
              placeholder={t('otherInfoPlaceholder')}
              {...form.register('otherInfo')}
            />
            {!!form.formState.errors.otherInfo?.message && (
              <Field.Error>
                {form.formState.errors.otherInfo?.message}
              </Field.Error>
            )}
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('brandValues')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_brand-values"
              placeholder={t('brandValuesPlaceholder')}
              {...form.register('brandValues')}
            />
            {!!form.formState.errors.brandValues?.message && (
              <Field.Error>
                {form.formState.errors.brandValues?.message}
              </Field.Error>
            )}
            <Controller
              name="brandValuesKeywords"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <div className="tw-mt-2">
                  <Select
                    isMulti
                    value={value?.map((keyword) => ({
                      value: keyword,
                      label: keyword,
                    }))}
                    onChange={(options) =>
                      onChange(options?.map((option) => option.value) || [])
                    }
                    placeholder={t('addKeywords')}
                    isCreatable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('coreValues')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_core-values"
              placeholder={t('coreValuesPlaceholder')}
              {...form.register('coreValues')}
            />
            {!!form.formState.errors.coreValues?.message && (
              <Field.Error>
                {form.formState.errors.coreValues?.message}
              </Field.Error>
            )}
            <Controller
              name="coreValuesKeywords"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <div className="tw-mt-2">
                  <Select
                    isMulti
                    value={value?.map((keyword) => ({
                      value: keyword,
                      label: keyword,
                    }))}
                    onChange={(options) =>
                      onChange(options?.map((option) => option.value) || [])
                    }
                    placeholder={t('addKeywords')}
                    isCreatable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('toneOfVoice')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_tone-of-voice"
              placeholder={t('toneOfVoicePlaceholder')}
              {...form.register('toneOfVoice')}
            />
            {!!form.formState.errors.toneOfVoice?.message && (
              <Field.Error>
                {form.formState.errors.toneOfVoice?.message}
              </Field.Error>
            )}
            <Controller
              name="toneOfVoiceKeywords"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <div className="tw-mt-2">
                  <Select
                    isMulti
                    value={value?.map((keyword) => ({
                      value: keyword,
                      label: keyword,
                    }))}
                    onChange={(options) =>
                      onChange(options?.map((option) => option.value) || [])
                    }
                    placeholder={t('addKeywords')}
                    isCreatable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('wordsToEmbrace')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_words-to-embrace"
              placeholder={t('wordsToEmbracePlaceholder')}
              {...form.register('wordsToEmbrace')}
            />
            {!!form.formState.errors.wordsToEmbrace?.message && (
              <Field.Error>
                {form.formState.errors.wordsToEmbrace?.message}
              </Field.Error>
            )}
            <Controller
              name="wordsToEmbraceKeywords"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <div className="tw-mt-2">
                  <Select
                    isMulti
                    value={value?.map((keyword) => ({
                      value: keyword,
                      label: keyword,
                    }))}
                    onChange={(options) =>
                      onChange(options?.map((option) => option.value) || [])
                    }
                    placeholder={t('addKeywords')}
                    isCreatable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('visualIdentity', 'Visual Identity')}</Field.Label>
            <Field.Caption>
              {t(
                'visualIdentityCaption',
                'Describe your brand colors, fonts, shapes, and visual style to help AI preserve brand consistency.',
              )}
            </Field.Caption>
            <TextArea
              data-name="brand-hub_manage-brand-profile_visual-identity"
              placeholder={t(
                'visualIdentityPlaceholder',
                'e.g., Primary: #FF5733, Secondary: #2146EC. Font: Montserrat Bold for headlines, Open Sans for body. Round shapes, minimal style.',
              )}
              {...form.register('visualIdentity')}
            />
            {!!form.formState.errors.visualIdentity?.message && (
              <Field.Error>
                {form.formState.errors.visualIdentity?.message}
              </Field.Error>
            )}
            <Controller
              name="visualIdentityKeywords"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <div className="tw-mt-2">
                  <Select
                    isMulti
                    value={value?.map((keyword) => ({
                      value: keyword,
                      label: keyword,
                    }))}
                    onChange={(options) =>
                      onChange(options?.map((option) => option.value) || [])
                    }
                    placeholder={t('addKeywords')}
                    isCreatable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('mainCompetitors')}</Field.Label>
            <TextArea
              data-name="brand-hub_manage-brand-profile_main-competitors"
              placeholder={t('mainCompetitorsPlaceholder')}
              {...form.register('mainCompetitors')}
            />
            {!!form.formState.errors.mainCompetitors?.message && (
              <Field.Error>
                {form.formState.errors.mainCompetitors?.message}
              </Field.Error>
            )}
            <Controller
              name="mainCompetitorsKeywords"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <div className="tw-mt-2">
                  <Select
                    isMulti
                    value={value?.map((keyword) => ({
                      value: keyword,
                      label: keyword,
                    }))}
                    onChange={(options) =>
                      onChange(options?.map((option) => option.value) || [])
                    }
                    placeholder={t('addKeywords')}
                    isCreatable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('brandHub.contentFormat')}</Field.Label>
            <Controller
              name="contentFormat"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <Select
                  isMulti
                  value={value?.map((format) => ({
                    value: format,
                    label: format,
                  }))}
                  onChange={(options) =>
                    onChange(options?.map((option) => option.value) || [])
                  }
                  placeholder={t('brandHub.addContentFormats')}
                  isCreatable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (styles) => ({
                      ...styles,
                      zIndex: 1010,
                    }),
                  }}
                />
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('brandHub.topics')}</Field.Label>
            <Controller
              name="topics"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <Select
                  isMulti
                  value={value?.map((topic) => ({
                    value: topic,
                    label: topic,
                  }))}
                  onChange={(options) =>
                    onChange(options?.map((option) => option.value) || [])
                  }
                  placeholder={t('brandHub.addTopics')}
                  isCreatable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (styles) => ({
                      ...styles,
                      zIndex: 1010,
                    }),
                  }}
                />
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label>{t('aiChatModel', 'AI Text Model')}</Field.Label>
            <Field.Caption>
              {t(
                'aiChatModelCaption',
                "Only change if you know what you're doing",
              )}
            </Field.Caption>
            <Controller
              name="aiChatModel"
              control={form.control}
              render={({ field: { onChange, value, ref } }) => {
                const groupedOptions = createGroupedModelOptions(
                  aiChatModelsQuery.data?.chat,
                );
                const allOptions = groupedOptions.flatMap((g) => g.options);
                const selectedValue =
                  allOptions.find((option) => option.value === value) ?? null;

                return (
                  <div
                    ref={ref}
                    tabIndex={0}
                    data-name="brand-hub_manage-brand-profile_ai-chat-model_select-wrapper"
                    data-cy="brand-hub_manage-brand-profile_ai-chat-model_select-wrapper"
                  >
                    <Select
                      isClearable
                      options={groupedOptions}
                      onChange={(option) => {
                        onChange(option?.value ?? null);
                      }}
                      value={selectedValue}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      placeholder={t('defaultAiModel', 'Auto (recommended)')}
                      isLoading={aiChatModelsQuery.isLoading}
                      styles={{
                        menuPortal: (styles) => ({
                          ...styles,
                          zIndex: 1010,
                        }),
                      }}
                    />
                  </div>
                );
              }}
            />
          </Field.Group>
          <Field.Group>
            <Field.Label>{t('aiImageModel', 'AI Image Model')}</Field.Label>
            <Field.Caption>
              {t(
                'aiImageModelCaption',
                "Only change if you know what you're doing",
              )}
            </Field.Caption>
            <Controller
              name="aiImageModel"
              control={form.control}
              render={({ field: { onChange, value, ref } }) => {
                const groupedOptions = createGroupedModelOptions(
                  aiImageModelsQuery.data?.image,
                );
                const allOptions = groupedOptions.flatMap((g) => g.options);
                const selectedValue =
                  allOptions.find((option) => option.value === value) ?? null;

                return (
                  <div
                    ref={ref}
                    tabIndex={0}
                    data-name="brand-hub_manage-brand-profile_ai-image-model_select-wrapper"
                    data-cy="brand-hub_manage-brand-profile_ai-image-model_select-wrapper"
                  >
                    <Select
                      isClearable
                      options={groupedOptions}
                      onChange={(option) => {
                        onChange(option?.value ?? null);
                      }}
                      value={selectedValue}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      placeholder={t('defaultAiModel', 'Auto (recommended)')}
                      isLoading={aiImageModelsQuery.isLoading}
                      styles={{
                        menuPortal: (styles) => ({
                          ...styles,
                          zIndex: 1010,
                        }),
                      }}
                    />
                  </div>
                );
              }}
            />
          </Field.Group>
        </Field.Group>
        {profileId && (
          <>
            <Divider />
            <Field.Group>
              <div>
                <Button
                  data-name="brand-hub_manage-brand-profile_delete"
                  variant="danger"
                  isLoading={deleteProfile.isLoading}
                  onClick={() => onDeleteProfile(profileId)}
                >
                  {t('deleteBrandProfile')}
                </Button>
              </div>
            </Field.Group>
          </>
        )}
      </Modal.Content>
      <Modal.Footer withDivider>
        <Button
          onClick={onClose}
          variant="secondary"
          data-name="brand-hub_manage-brand-profile_close"
        >
          {t('close')}
        </Button>
        <Tooltip
          content={
            !form.formState.isValid
              ? t('pleaseFillRequiredFormFields')
              : undefined
          }
        >
          <div>
            <Button
              type="submit"
              data-name="brand-hub_manage-brand-profile_save"
              disabled={!form.formState.isValid}
              isLoading={saveProfile.isLoading}
            >
              {t('save')}
            </Button>
          </div>
        </Tooltip>
      </Modal.Footer>
    </form>
  );
};

export default BrandHubManageProfileModalForm;
